/**
 * — jl-header.js
 * Horizon theme pattern: behaviour as Custom Elements / Web Components.
 * Defines <jl-header> and <jl-drawer>.
 */

/* ── Utility ─────────────────────────────────────────────── */
const focusable = (el) =>
  [...el.querySelectorAll(
    'a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])'
  )];

function trapFocus(el) {
  const items = focusable(el);
  if (!items.length) return;
  const first = items[0], last = items[items.length - 1];
  el._trapHandler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    }
  };
  el.addEventListener('keydown', el._trapHandler);
}

function releaseFocus(el) {
  if (el._trapHandler) el.removeEventListener('keydown', el._trapHandler);
}

/* ── <jl-drawer> ─────────────────────────────────────────── */
class JlDrawer extends HTMLElement {
  constructor() {
    super();
    this._open = false;
  }

  connectedCallback() {
    // Close button inside drawer
    this.querySelector('[data-jl-drawer-close]')?.addEventListener('click', () => this.close());

    // Mobile sub-menu expand toggles
    this.querySelectorAll('[data-jl-expand]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        // collapse all siblings first
        this.querySelectorAll('[data-jl-expand]').forEach((b) => {
          if (b !== btn) {
            b.setAttribute('aria-expanded', 'false');
            b.nextElementSibling && (b.nextElementSibling.hidden = true);
          }
        });
        btn.setAttribute('aria-expanded', String(!expanded));
        btn.nextElementSibling && (btn.nextElementSibling.hidden = expanded);
      });
    });
  }

  open() {
    this._open = true;
    this.hidden = false;
    this.removeAttribute('aria-hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });
    });
    document.body.style.overflow = 'hidden';
    trapFocus(this);
    setTimeout(() => focusable(this)[0]?.focus(), 60);
  }

  close(returnFocus) {
    this._open = false;
    this.classList.remove('is-open');
    releaseFocus(this);
    document.body.style.overflow = '';
    document.querySelector('[data-jl-overlay]')?.classList.remove('is-visible');
    const dur = parseFloat(getComputedStyle(this).transitionDuration) * 1000 || 220;
    setTimeout(() => {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
    }, dur);
    returnFocus?.focus();
  }
}

customElements.define('jl-drawer', JlDrawer);

/* ── <jl-header> ─────────────────────────────────────────── */
class JlHeader extends HTMLElement {
  connectedCallback() {
    this._drawer   = document.getElementById('jl-drawer');
    this._overlay  = document.querySelector('[data-jl-overlay]');
    this._menuBtn  = this.querySelector('[data-jl-menu-toggle]');
    this._searchBtn = this.querySelector('[data-jl-search-toggle]');
    this._searchBar = this.querySelector('#jl-search-bar') || document.getElementById('jl-search-bar');

    this._menuBtn?.addEventListener('click', () => this._toggleDrawer());
    this._overlay?.addEventListener('click', () => this._closeAll());
    this._searchBtn?.addEventListener('click', () => this._toggleSearch());

    // Desktop dropdowns
    this.querySelectorAll('[data-jl-dropdown-toggle]').forEach((btn) => {
      const item = btn.closest('.jl-nav__item');
      const panel = document.getElementById(btn.getAttribute('aria-controls'));

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = btn.getAttribute('aria-expanded') === 'true';
        this._closeDropdowns();
        if (!open && panel) {
          btn.setAttribute('aria-expanded', 'true');
          panel.classList.add('is-open');
        }
      });

      // Hover (pointer: fine = mouse/trackpad, not touch)
      if (item) {
        item.addEventListener('mouseenter', () => {
          if (!matchMedia('(pointer: fine)').matches) return;
          this._closeDropdowns();
          btn.setAttribute('aria-expanded', 'true');
          panel?.classList.add('is-open');
        });
        item.addEventListener('mouseleave', () => {
          if (!matchMedia('(pointer: fine)').matches) return;
          btn.setAttribute('aria-expanded', 'false');
          panel?.classList.remove('is-open');
        });
      }
    });

    // Close dropdowns on outside click
    document.addEventListener('click', () => this._closeDropdowns());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      this._closeAll();
    });

    // Sticky scroll-direction detection
    if (this.classList.contains('jl-header--sticky-scroll')) {
      const group = document.getElementById('header-group');
      let last = window.scrollY;
      window.addEventListener('scroll', () => {
        const current = window.scrollY;
        const hide = current > last && current > 120;
        if (group) group.style.transform = hide ? 'translateY(-100%)' : '';
        last = current;
      }, { passive: true });
    }
  }

  _toggleDrawer() {
    if (!this._drawer) return;
    if (this._drawer._open) {
      this._drawer.close(this._menuBtn);
      this._overlay?.classList.remove('is-visible');
      this._menuBtn?.setAttribute('aria-expanded', 'false');
    } else {
      this._drawer.open();
      this._overlay?.classList.add('is-visible');
      this._menuBtn?.setAttribute('aria-expanded', 'true');
    }
  }

  _toggleSearch() {
    if (!this._searchBar) return;
    const open = this._searchBar.classList.contains('is-open');
    if (open) {
      this._searchBar.classList.remove('is-open');
      const dur = parseFloat(getComputedStyle(this._searchBar).transitionDuration) * 1000 || 220;
      setTimeout(() => {
        this._searchBar.hidden = true;
        this._searchBar.setAttribute('aria-hidden', 'true');
      }, dur);
      this._searchBtn?.setAttribute('aria-expanded', 'false');
      this._searchBtn?.setAttribute('aria-label', 'Search');
    } else {
      this._searchBar.hidden = false;
      this._searchBar.removeAttribute('aria-hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this._searchBar.classList.add('is-open');
        this._searchBar.querySelector('input')?.focus();
      }));
      this._searchBtn?.setAttribute('aria-expanded', 'true');
      this._searchBtn?.setAttribute('aria-label', 'Close search');
    }
  }

  _closeDropdowns() {
    this.querySelectorAll('[data-jl-dropdown-toggle]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      panel?.classList.remove('is-open');
    });
  }

  _closeAll() {
    if (this._drawer?._open) {
      this._drawer.close(this._menuBtn);
      this._overlay?.classList.remove('is-visible');
      this._menuBtn?.setAttribute('aria-expanded', 'false');
    }
    if (this._searchBar?.classList.contains('is-open')) {
      this._toggleSearch();
    }
    this._closeDropdowns();
  }
}

customElements.define('jl-header', JlHeader);
